import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import { useAuth } from '../App';
import {
  Chart as ChartJS, CategoryScale, LinearScale,
  PointElement, LineElement, Filler, Tooltip
} from 'chart.js';
import { apiCall } from '../App';
import { useToast } from '../context/ToastContext';
import { useTheme } from '../context/ThemeContext';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip);

function StatusBadge({ status }) {
  const s = status || 'pending';
  return (
    <span className={`status-badge ${s}`}>
      <span className="status-dot" />
      {s}
    </span>
  );
}

function StatCard({ label, value, sub, color, trend, trendLabel }) {
  return (
    <div className="stat-card">
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={color ? { color } : {}}>{value}</div>
      {trend !== undefined && (
        <div className={`stat-trend ${trend >= 0 ? 'trend-up' : 'trend-down'}`}>
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            {trend >= 0
              ? <path d="M2 7l3-4 3 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
              : <path d="M2 3l3 4 3-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
            }
          </svg>
          {trendLabel}
        </div>
      )}
      {!trend && sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

function IntegrationModal({ integration, onClose, onDelete, onToggle }) {
  const [deleting, setDeleting] = useState(false);
  const [toggling, setToggling] = useState(false);
  const { addToast } = useToast();

  async function handleDelete() {
    if (!window.confirm(`Delete "${integration.name}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await apiCall(`/api/integrations/${integration.id}`, { method: 'DELETE' });
      addToast(`${integration.name} deleted`, 'success');
      onDelete(integration.id);
      onClose();
    } catch (err) {
      addToast('Failed to delete: ' + err.message, 'error');
      setDeleting(false);
    }
  }

  async function handleToggle() {
    setToggling(true);
    try {
      await apiCall(`/api/integrations/${integration.id}`, {
        method: 'PATCH',
        body: { active: !integration.active },
      });
      addToast(`${integration.name} ${integration.active ? 'paused' : 'resumed'}`, 'success');
      onToggle(integration.id, !integration.active);
    } catch (err) {
      addToast('Failed to update: ' + err.message, 'error');
    } finally {
      setToggling(false);
    }
  }

  const upColor = integration.uptime >= 99 ? 'var(--green)' : integration.uptime >= 95 ? 'var(--amber)' : 'var(--red)';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div className="modal-title">{integration.name}</div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2, fontFamily: 'var(--mono)' }}>{integration.url}</div>
          </div>
          <button className="modal-close" onClick={onClose}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
          </button>
        </div>

        <div className="modal-body">
          <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
            <StatusBadge status={integration.status} />
            <span style={{ fontSize: 11, padding: '3px 9px', borderRadius: 20, background: 'var(--surface)', color: 'var(--text-2)', fontFamily: 'var(--mono)' }}>{integration.type}</span>
            <span style={{ fontSize: 11, padding: '3px 9px', borderRadius: 20, background: 'var(--surface)', color: 'var(--text-2)' }}>Every {integration.pollIntervalSeconds}s</span>
            {!integration.active && <span style={{ fontSize: 11, padding: '3px 9px', borderRadius: 20, background: 'var(--amber-dim)', color: 'var(--amber)' }}>Paused</span>}
          </div>

          <div className="metric-row"><span className="metric-label">Uptime</span><span className="metric-value" style={{ color: upColor }}>{integration.uptime || 0}%</span></div>
          <div className="metric-row"><span className="metric-label">Avg response time</span><span className="metric-value">{integration.avgResponseTime || 0}ms</span></div>
          <div className="metric-row"><span className="metric-label">Expected status</span><span className="metric-value">{integration.expectedStatus || 200}</span></div>
          <div className="metric-row"><span className="metric-label">Last checked</span><span className="metric-value">{integration.lastChecked ? new Date(integration.lastChecked).toLocaleTimeString() : 'Never'}</span></div>
          {integration.lastError && <div className="metric-row"><span className="metric-label">Last error</span><span className="metric-value" style={{ color: 'var(--red)', fontSize: 11 }}>{integration.lastError}</span></div>}
          {integration.trendData?.trending && (
            <div style={{ marginTop: 12, padding: '10px 12px', background: 'var(--amber-dim)', borderRadius: 8, fontSize: 12, color: 'var(--amber)', border: '0.5px solid rgba(255,159,10,0.2)' }}>
              Response time trending up {integration.trendData.pctIncrease}% — predictive alert active
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </button>
          <button className="btn-secondary" onClick={handleToggle} disabled={toggling}>
            {toggling ? '...' : integration.active ? 'Pause monitoring' : 'Resume monitoring'}
          </button>
          <button className="btn-primary" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [checkingId, setCheckingId] = useState(null);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const { addToast } = useToast();
  const { isDark } = useTheme();
  const { user } = useAuth();

  const fetchData = useCallback(async () => {
    try {
      const [dashRes, alertRes] = await Promise.all([
        apiCall('/api/dashboard'),
        apiCall('/api/alerts?limit=10'),
      ]);
      setData(dashRes);
      setAlerts(alertRes.alerts || []);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  async function handleManualCheck(id, name) {
    setCheckingId(id);
    try {
      const result = await apiCall(`/api/integrations/${id}/check`, { method: 'POST' });
      await fetchData();
      const status = result.result?.success ? 'healthy' : 'down';
      addToast(`${name}: ${status === 'healthy' ? 'Check passed' : 'Check failed'} (${result.result?.responseTime}ms)`, status === 'healthy' ? 'success' : 'error');
    } catch (err) {
      addToast('Check failed: ' + err.message, 'error');
    } finally {
      setCheckingId(null);
    }
  }

  async function acknowledgeAlert(alertId) {
    try {
      await apiCall(`/api/alerts/${alertId}/acknowledge`, { method: 'PATCH' });
      setAlerts(prev => prev.filter(a => a.id !== alertId));
      addToast('Alert dismissed', 'success');
    } catch (err) {
      addToast('Failed to dismiss alert', 'error');
    }
  }

  function handleDeleteIntegration(id) {
    setData(prev => ({
      ...prev,
      integrations: prev.integrations.filter(i => i.id !== id),
      stats: { ...prev.stats, totalIntegrations: prev.stats.totalIntegrations - 1 },
    }));
  }

  function handleToggleIntegration(id, active) {
    setData(prev => ({
      ...prev,
      integrations: prev.integrations.map(i => i.id === id ? { ...i, active } : i),
    }));
    setSelectedIntegration(prev => prev ? { ...prev, active } : null);
  }

  const gridColor = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)';
  const tickColor = isDark ? '#636366' : '#98989D';
  const tooltipBg = isDark ? '#2C2C2E' : '#FFFFFF';

  const chartOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { mode: 'index', intersect: false, backgroundColor: tooltipBg, borderColor: 'rgba(0,0,0,0.1)', borderWidth: 1, titleColor: isDark ? '#F5F5F7' : '#1D1D1F', bodyColor: isDark ? '#98989D' : '#6E6E73', padding: 10, cornerRadius: 8 }
    },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 10, family: 'JetBrains Mono' }, color: tickColor }, border: { display: false } },
      y: { grid: { color: gridColor }, ticks: { font: { size: 10, family: 'JetBrains Mono' }, color: tickColor }, beginAtZero: true, border: { display: false } },
    },
  };

  if (loading) return <div className="page-loading"><div className="spinner" /><p>Loading dashboard...</p></div>;
  if (error) return <div className="page-error"><h2>Connection error</h2><p>{error}</p><button className="btn-primary" onClick={fetchData}>Retry</button></div>;

  const { stats, integrations, timeline } = data || {};
  const unackedAlerts = alerts.filter(a => !a.acknowledged);
  const statusPageUrl = `${window.location.origin}/status/${user?.uid}`;

  const chartData = {
    labels: (timeline || []).map(t => `${new Date(t.hour).getHours()}:00`),
    datasets: [
      { label: 'Success', data: (timeline||[]).map(t=>t.success), borderColor: 'var(--green)', backgroundColor: isDark ? 'rgba(48,209,88,0.06)' : 'rgba(48,209,88,0.08)', fill: true, tension: 0.4, pointRadius: 0, borderWidth: 1.5 },
      { label: 'Failed', data: (timeline||[]).map(t=>t.failed), borderColor: 'var(--red)', backgroundColor: isDark ? 'rgba(255,69,58,0.05)' : 'rgba(255,69,58,0.06)', fill: true, tension: 0.4, pointRadius: 0, borderWidth: 1.5 },
    ],
  };

  const recentActivity = alerts.slice(0, 6).map(a => ({
    type: a.type === 'failure' ? 'red' : a.type === 'slow' || a.type === 'degrading' ? 'amber' : 'green',
    text: a.type === 'failure' ? `<strong>${a.integrationName}</strong> went down` : a.type === 'slow' ? `<strong>${a.integrationName}</strong> responding slowly` : a.type === 'degrading' ? `<strong>${a.integrationName}</strong> degrading` : `<strong>${a.integrationName}</strong> recovered`,
    time: new Date(a.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
  }));

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-sub">
            {lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString()} · ` : ''}
            Auto-refreshes every 30s
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="btn-secondary"
            onClick={() => { navigator.clipboard?.writeText(statusPageUrl); addToast('Status page URL copied!', 'success'); }}
            title="Copy status page URL"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.2"/><path d="M3 7h8M7 3c-1.5 1.5-1.5 6 0 8M7 3c1.5 1.5 1.5 6 0 8" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/></svg>
            Status page
          </button>
          <Link to="/integrations/add" className="btn-primary">
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><circle cx="6.5" cy="6.5" r="5.5" stroke="white" strokeWidth="1.2"/><path d="M6.5 4v5M4 6.5h5" stroke="white" strokeWidth="1.3" strokeLinecap="round"/></svg>
            Add integration
          </Link>
        </div>
      </div>

      <div className="stats-grid">
        <StatCard label="Total" value={stats?.totalIntegrations || 0} sub="monitored" />
        <StatCard label="Healthy" value={stats?.healthyCount || 0} color="var(--green)" sub="running normally" />
        <StatCard label="Down" value={stats?.failingCount || 0} color={stats?.failingCount > 0 ? 'var(--red)' : undefined} sub={stats?.failingCount > 0 ? 'needs attention' : 'all clear'} />
        <StatCard label="Avg uptime" value={`${stats?.avgUptime || 100}%`} color={stats?.avgUptime >= 99 ? 'var(--green)' : stats?.avgUptime >= 95 ? 'var(--amber)' : 'var(--red)'} />
        <StatCard label="Response" value={`${stats?.avgResponseTime || 0}ms`} sub="average" />
        <StatCard label="Alerts" value={stats?.unackedAlerts || 0} color={stats?.unackedAlerts > 0 ? 'var(--red)' : undefined} sub="unacknowledged" />
      </div>

      <div className="dash-grid">
        <div className="dash-main">
          {unackedAlerts.length > 0 && (
            <div className="card">
              <div className="card-header">
                <span className="card-title">Active alerts</span>
                <span style={{ fontSize: 11, background: 'var(--red-dim)', color: 'var(--red)', padding: '2px 8px', borderRadius: 20, fontFamily: 'var(--mono)', fontWeight: 600 }}>{unackedAlerts.length}</span>
              </div>
              {unackedAlerts.map(alert => (
                <div key={alert.id} className={`alert-banner ${alert.type}`}>
                  <div className={`alert-dot ${alert.type}`} />
                  <div className="alert-info">
                    <div className="alert-name">{alert.integrationName}</div>
                    <div className="alert-msg">
                      {alert.type === 'failure' ? `Down — ${alert.error || 'check failed'}` :
                       alert.type === 'slow' ? `Slow — ${alert.responseTime}ms response time` :
                       alert.type === 'degrading' ? `Degrading — response time up ${alert.trendData?.pctIncrease || ''}%` :
                       'Recovered — back online'}
                    </div>
                    <div className="alert-time">{new Date(alert.createdAt).toLocaleTimeString()}</div>
                  </div>
                  <button className="ack-btn" onClick={() => acknowledgeAlert(alert.id)}>Dismiss</button>
                </div>
              ))}
            </div>
          )}

          <div className="card">
            <div className="card-header">
              <span className="card-title">24-hour check history</span>
              <div className="chart-legend">
                <span className="legend-item"><span className="legend-dot" style={{ background: 'var(--green)' }} />Success</span>
                <span className="legend-item"><span className="legend-dot" style={{ background: 'var(--red)' }} />Failed</span>
              </div>
            </div>
            <div style={{ height: 160 }}>
              <Line data={chartData} options={chartOptions} />
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <span className="card-title">Integrations</span>
              <span className="card-sub">{integrations?.length || 0} total</span>
            </div>
            {!integrations?.length ? (
              <div className="empty-state">
                <div className="empty-icon">
                  <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><circle cx="11" cy="11" r="9" stroke="var(--text-3)" strokeWidth="1.5"/><path d="M11 7v4l3 2" stroke="var(--text-3)" strokeWidth="1.5" strokeLinecap="round"/></svg>
                </div>
                <div className="empty-title">No integrations yet</div>
                <div className="empty-sub">Add your first integration and IntegratePulse will start monitoring it within 60 seconds.</div>
                <Link to="/integrations/add" className="btn-primary">Add your first integration</Link>
              </div>
            ) : (
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Status</th>
                      <th>Uptime</th>
                      <th>Avg response</th>
                      <th>Last checked</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {integrations.map(i => (
                      <tr key={i.id} style={{ cursor: 'pointer' }} onClick={() => setSelectedIntegration(i)}>
                        <td>
                          <div className="int-name">{i.name}</div>
                          <div className="int-url">{i.url}</div>
                        </td>
                        <td><StatusBadge status={i.status} /></td>
                        <td>
                          <div className="uptime-wrap">
                            <div className="uptime-bar">
                              <div className="uptime-fill" style={{ width: `${i.uptime||0}%`, background: i.uptime>=99?'var(--green)':i.uptime>=95?'var(--amber)':'var(--red)' }} />
                            </div>
                            <span className="uptime-pct">{i.uptime||0}%</span>
                          </div>
                        </td>
                        <td className="mono">{i.avgResponseTime ? `${i.avgResponseTime}ms` : '—'}</td>
                        <td className="muted">{i.lastChecked ? new Date(i.lastChecked).toLocaleTimeString() : 'Never'}</td>
                        <td onClick={e => e.stopPropagation()}>
                          <button
                            className={`action-btn ${checkingId === i.id ? 'loading' : ''}`}
                            onClick={() => handleManualCheck(i.id, i.name)}
                            disabled={checkingId === i.id}
                          >
                            {checkingId === i.id ? 'Checking...' : 'Check now'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className="dash-side">
          <div className="card">
            <div className="card-header" style={{ marginBottom: 12 }}>
              <span className="card-title">Quick stats</span>
            </div>
            <div className="side-stats-grid">
              <div className="side-stat">
                <div className="side-stat-label">Response</div>
                <div className="side-stat-val" style={{ fontSize: 18 }}>
                  {stats?.avgResponseTime||0}<span style={{ fontSize: 11, color: 'var(--text-3)' }}>ms</span>
                </div>
              </div>
              <div className="side-stat">
                <div className="side-stat-label">Alerts</div>
                <div className="side-stat-val" style={{ color: unackedAlerts.length > 0 ? 'var(--red)' : 'var(--text)' }}>{unackedAlerts.length}</div>
              </div>
              <div className="side-stat">
                <div className="side-stat-label">Healthy</div>
                <div className="side-stat-val" style={{ color: 'var(--green)' }}>{stats?.healthyCount||0}</div>
              </div>
              <div className="side-stat">
                <div className="side-stat-label">Down</div>
                <div className="side-stat-val" style={{ color: stats?.failingCount > 0 ? 'var(--red)' : 'var(--text)' }}>{stats?.failingCount||0}</div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header" style={{ marginBottom: 8 }}>
              <span className="card-title">Recent activity</span>
            </div>
            {recentActivity.length === 0 ? (
              <div style={{ padding: '16px 0', textAlign: 'center', fontSize: 12, color: 'var(--text-3)' }}>
                All quiet — no recent alerts
              </div>
            ) : (
              <div className="activity-list">
                {recentActivity.map((a, i) => (
                  <div key={i} className="activity-item">
                    <div className={`act-dot ${a.type}`} />
                    <div>
                      <div className="act-text" dangerouslySetInnerHTML={{ __html: a.text }} />
                      <div className="act-time">{a.time}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="card" style={{ background: 'var(--blue-dim)', borderColor: 'rgba(10,132,255,0.2)' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--blue)', marginBottom: 5 }}>Your status page</div>
            <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 12, lineHeight: 1.6, fontFamily: 'var(--mono)', wordBreak: 'break-all' }}>
              {statusPageUrl}
            </div>
            <button
              className="btn-primary"
              style={{ width: '100%', justifyContent: 'center', fontSize: 12, background: 'var(--blue)' }}
              onClick={() => window.open(statusPageUrl, '_blank')}
            >
              Open status page
            </button>
          </div>

          <div className="promo-card">
            <div className="promo-title">Upgrade to Pro</div>
            <div className="promo-sub">25 integrations, 30s polling, AI monitoring, multi-region checks, and Slack alerts.</div>
            <Link to="/billing" className="btn-primary" style={{ width: '100%', justifyContent: 'center', fontSize: 12 }}>View plans →</Link>
          </div>
        </div>
      </div>

      {selectedIntegration && (
        <IntegrationModal
          integration={selectedIntegration}
          onClose={() => setSelectedIntegration(null)}
          onDelete={handleDeleteIntegration}
          onToggle={handleToggleIntegration}
        />
      )}
    </div>
  );
}
